import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { goodsIntakeApi } from '../api/goods-intake-api';
import { ForbiddenError } from '../api/errors';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { isOpenReceipt, receiptSourceLabels, type ExpectedReceipt } from '../domain/goods-intake';
import { intakeErrorMessage } from '../features/goods-intake/error-message';
import { NewReceiptDrawer } from '../features/goods-intake/NewReceiptDrawer';
import { ReceiptStateBadge } from '../features/goods-intake/ReceiptStateBadge';
import { ScanField, type ScanOutcome } from '../features/goods-intake/ScanField';
import { INTAKE_QUERY_KEYS } from '../features/goods-intake/use-intake-queries';
import { useStockReferences } from '../features/stock/use-stock-references';

const views = ['open', 'received', 'cancelled'] as const;
type View = (typeof views)[number];
const viewLabels: Record<View, string> = {
  open: 'To receive',
  received: 'Received',
  cancelled: 'Cancelled',
};

function inView(receipt: ExpectedReceipt, view: View): boolean {
  if (view === 'open') return isOpenReceipt(receipt);
  return view === 'received' ? receipt.state === 'POSTED' : receipt.state === 'CANCELLED';
}

function isView(value: string | null): value is View {
  return views.some((view) => view === value);
}

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

/** Scan-first Goods Intake (tidaltech/koi#8101). */
export function GoodsIntakePage() {
  const navigate = useNavigate();
  const references = useStockReferences();
  const [params, setParams] = useSearchParams();
  const viewParam = params.get('view');
  const view: View = isView(viewParam) ? viewParam : 'open';
  const query = params.get('q') ?? '';
  const [creating, setCreating] = useState<'expected' | 'arrived' | null>(null);
  const [matches, setMatches] = useState<ExpectedReceipt[]>([]);

  const receipts = useQuery({
    queryKey: INTAKE_QUERY_KEYS.receipts,
    queryFn: () => goodsIntakeApi.listReceipts(),
  });

  function setParam(name: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next);
  }

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (receipts.data ?? [])
      .filter((receipt) => inView(receipt, view))
      .filter(
        (receipt) =>
          !needle ||
          [receipt.supplier, receipt.supplierReference, ...receipt.identifiers]
            .join(' ')
            .toLowerCase()
            .includes(needle),
      )
      .sort((a, b) =>
        view === 'open'
          ? (a.expectedAt ?? '9999').localeCompare(b.expectedAt ?? '9999')
          : (b.postedAt ?? b.lastModified ?? '').localeCompare(a.postedAt ?? a.lastModified ?? ''),
      );
  }, [receipts.data, view, query]);

  async function find(code: string): Promise<ScanOutcome> {
    setMatches([]);
    try {
      const found = await goodsIntakeApi.findReceipts(code);
      if (found.length === 1 && found[0]) {
        void navigate(`/goods-intake/${encodeURIComponent(found[0].id)}`);
        return { tone: 'accepted', text: `Opening ${found[0].supplier ?? 'the receipt'}.` };
      }
      if (found.length > 1) {
        setMatches(found);
        return {
          tone: 'accepted',
          text: `${found.length} open receipts match ${code}. Choose one below.`,
        };
      }
      return {
        tone: 'rejected',
        text: `No open receipt matches ${code}.`,
        action: (
          <button type="button" onClick={() => setCreating('arrived')}>
            Receive without a manifest
          </button>
        ),
      };
    } catch (error) {
      return { tone: 'rejected', text: intakeErrorMessage(error, 'The lookup failed. Try again.') };
    }
  }

  if (receipts.isLoading || references.loading) {
    return <PageLoadingState label="Loading receipts…" />;
  }
  if (receipts.error instanceof ForbiddenError) {
    return (
      <div className="empty-state">
        <strong>Staff access required</strong>
        <p>
          Sign in with a <span className="mono">@tidalx.ai</span> account to receive goods.
        </p>
      </div>
    );
  }
  if (receipts.isError || references.error) {
    return (
      <ErrorState
        title="Receipts could not be loaded"
        rpcName="ListExpectedReceipts"
        onRetry={() => {
          void receipts.refetch();
          references.refetch();
        }}
      />
    );
  }

  function receiptCard(receipt: ExpectedReceipt) {
    const counted = receipt.lines.filter((line) => line.receivedQuantity !== undefined).length;
    return (
      <li key={receipt.id} className="order-card intake-card">
        <div className="order-card-heading">
          <Link to={`/goods-intake/${encodeURIComponent(receipt.id)}`} className="order-card-title">
            {receipt.supplier ?? 'Unknown supplier'}
            {receipt.supplierReference ? ` · ${receipt.supplierReference}` : ''}
          </Link>
          <ReceiptStateBadge state={receipt.state} />
        </div>
        <p className="order-card-meta">
          {references.siteName(receipt.siteId)}
          {receipt.expectedAt
            ? ` · expected ${dateFormat.format(new Date(receipt.expectedAt))}`
            : ''}
          {' · '}
          {receiptSourceLabels[receipt.source]}
        </p>
        <p className="order-card-meta">
          {receipt.lines.length} {receipt.lines.length === 1 ? 'line' : 'lines'}
          {receipt.state === 'RECEIVING' ? ` · ${counted} counted` : ''}
          {receipt.discrepancies.length > 0
            ? ` · ${receipt.discrepancies.length} ${receipt.discrepancies.length === 1 ? 'discrepancy' : 'discrepancies'}`
            : ''}
        </p>
        {receipt.identifiers.length > 0 ? (
          <p className="order-card-meta mono">{receipt.identifiers.join(', ')}</p>
        ) : null}
      </li>
    );
  }

  return (
    <>
      <PageHeader
        title="Goods intake"
        description="Scan a parcel, pallet or delivery note to count what arrived and put it into stock."
        actions={
          <>
            <button type="button" onClick={() => setCreating('expected')}>
              New expected receipt
            </button>
            <button type="button" className="primary" onClick={() => setCreating('arrived')}>
              Receive without a manifest
            </button>
          </>
        }
      />
      {creating ? (
        <NewReceiptDrawer
          mode={creating}
          references={references}
          onClose={() => setCreating(null)}
          onCreated={(receipt, notice) =>
            void navigate(`/goods-intake/${encodeURIComponent(receipt.id)}`, {
              state: notice ? { notice } : undefined,
            })
          }
        />
      ) : null}

      <section className="scan-panel intake-find" aria-labelledby="intake-find-heading">
        <h2 id="intake-find-heading">Find a delivery</h2>
        <ScanField
          id="intake-find"
          label="Parcel, pallet or delivery note number"
          hint="Scanners type the number and press Enter. Spaces and dashes don’t matter."
          autoFocus
          onScan={find}
        />
        {matches.length > 1 ? <ul className="order-cards">{matches.map(receiptCard)}</ul> : null}
      </section>

      <div className="region-tabs" role="tablist" aria-label="Receipt state">
        {views.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={view === candidate}
            className={`region-tab${view === candidate ? ' is-active' : ''}`}
            onClick={() => setParam('view', candidate === 'open' ? '' : candidate)}
          >
            {viewLabels[candidate]}{' '}
            <span className="tab-count">
              {(receipts.data ?? []).filter((receipt) => inView(receipt, candidate)).length}
            </span>
          </button>
        ))}
      </div>

      <section className="reference-workspace" aria-label="Receipts">
        <form
          className="requests-controls"
          role="search"
          aria-label="Filter receipts"
          onSubmit={(event) => event.preventDefault()}
        >
          <label className="search-field">
            <span>Search</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setParam('q', event.target.value)}
              placeholder="Supplier, order or parcel number"
              aria-label="Search receipts"
            />
          </label>
        </form>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <strong>
              {view === 'open' ? 'Nothing is waiting to be received.' : 'No receipts here yet.'}
            </strong>
            {view === 'open' ? (
              <button type="button" className="primary" onClick={() => setCreating('arrived')}>
                Receive without a manifest
              </button>
            ) : null}
          </div>
        ) : (
          <ul className="order-cards">{filtered.map(receiptCard)}</ul>
        )}
      </section>
    </>
  );
}
